import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Checkbox,
  MenuItem,
  alpha
} from '@mui/material';
import {
  Payment as PaymentIcon,
  TrendingUp,
  PendingActions,
  CheckCircle,
  Warning,
  Search,
  Close as CloseIcon,
  Person,
  History,
  CalendarToday,
  AccountBalanceWallet,
  MenuBook
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const FundCollectorDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [openSubjectDialog, setOpenSubjectDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [studentData, setStudentData] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openAddDateDialog, setOpenAddDateDialog] = useState(false);
  const [newPaymentDate, setNewPaymentDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [remarks, setRemarks] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  const grades = [6, 7, 8, 9, 10, 11];
  const subjects = ['Mathematics', 'Science'];

  useEffect(() => {
    // Show subject selection dialog if no subject selected
    if (!selectedSubject) {
      setOpenSubjectDialog(true);
    } else {
      fetchStats();
    }
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      fetchStats();
      setSelectedPayments([]);
    }
  }, [selectedSubject]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedSubject) params.subject = selectedSubject;
      
      const response = await axios.get(`${API_URL}/fees/stats`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
    }
  };

  const searchStudent = async () => {
    if (!searchTerm.trim()) {
      setAlert({ show: true, message: 'Please enter student ID or name', severity: 'warning' });
      return;
    }

    if (!selectedSubject) {
      setAlert({ show: true, message: 'Please select a subject first', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/fees/search-student`, {
        params: { 
          search: searchTerm,
          subject: selectedSubject 
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStudentData(response.data);
      setSelectedPayments([]);
      setAlert({ show: true, message: 'Student found!', severity: 'success' });
    } catch (error) {
      setStudentData(null);
      setAlert({
        show: true,
        message: error.response?.data?.error || 'Student not found',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setOpenSubjectDialog(false);
  };

  const handleChangeSubject = () => {
    setSelectedSubject('');
    setStudentData(null);
    setSearchTerm('');
    setOpenSubjectDialog(true);
  };

  const handleSelectPayment = (paymentId) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        return prev.filter(id => id !== paymentId);
      } else {
        return [...prev, paymentId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredUnpaidDays = studentData?.unpaid_days || [];

    if (selectedPayments.length === filteredUnpaidDays.length && filteredUnpaidDays.length > 0) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(filteredUnpaidDays.map(p => p.id));
    }
  };

  const handleMarkPayments = async () => {
    if (selectedPayments.length === 0) {
      setAlert({ show: true, message: 'Please select at least one payment', severity: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const paymentsToMark = studentData.unpaid_days.filter(p => selectedPayments.includes(p.id));

      await axios.post(`${API_URL}/fees/mark-bulk-payment`, {
        student_id: studentData.student.id,
        class_id: paymentsToMark[0].class_id,
        subject: selectedSubject,
        days: paymentsToMark.map(p => ({
          class_date: p.class_date,
          amount: p.amount
        })),
        payment_method: paymentMethod,
        remarks: remarks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ show: true, message: 'Payments marked successfully!', severity: 'success' });
      setOpenPaymentDialog(false);
      setRemarks('');
      setSelectedPayments([]);
      // Refresh student data
      await searchStudent();
      fetchStats();
    } catch (error) {
      setAlert({ show: true, message: 'Error marking payments', severity: 'error' });
    }
  };

  const handleAddPaymentDate = async () => {
    if (!newPaymentDate) {
      setAlert({ show: true, message: 'Please select a date', severity: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // Get subject fee amount (default amount or from settings)
      const amount = 100; // Default fee amount, can be made configurable

      // Create new payment record using the already selected subject
      await axios.post(`${API_URL}/fees/mark-payment`, {
        student_id: studentData.student.id,
        subject: selectedSubject,
        class_date: newPaymentDate,
        amount: amount,
        payment_status: 'pending'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ show: true, message: 'Payment date added successfully!', severity: 'success' });
      setOpenAddDateDialog(false);
      setNewPaymentDate('');
      // Refresh student data
      await searchStudent();
    } catch (error) {
      setAlert({ show: true, message: 'Error adding payment date', severity: 'error' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 1
                }}
              >
                Fund Collector Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Search and manage student fee payments
              </Typography>
            </Box>
           
          </Box>
        </Box>

        {/* Alert */}
        {alert.show && (
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, show: false })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Subject Filter Banner */}
        {selectedSubject && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              background: alpha('#667eea', 0.95),
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MenuBook sx={{ color: 'white', mr: 2, fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Collecting for: {selectedSubject}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  All payments will be marked for {selectedSubject} classes
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleChangeSubject}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              Change Subject
            </Button>
          </Paper>
        )}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PaymentIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>
                        Rs. {stats?.total_amount ? Number(stats.total_amount).toFixed(2) : '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Amount
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircle sx={{ fontSize: 40, color: '#10b981', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                        Rs. {stats?.collected_amount ? Number(stats.collected_amount).toFixed(2) : '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Collected
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PendingActions sx={{ fontSize: 40, color: '#f59e0b', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                        Rs. {stats?.pending_amount ? Number(stats.pending_amount).toFixed(2) : '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp sx={{ fontSize: 40, color: '#8b5cf6', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                        {stats?.collection_percentage ? Number(stats.collection_percentage).toFixed(1) : '0'}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Collection Rate
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Search Section */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Student
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Student ID or Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchStudent()}
                placeholder="Enter student ID or name..."
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={searchStudent}
                disabled={loading || !selectedSubject}
                startIcon={<Search />}
                sx={{
                  height: '56px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  }
                }}
              >
                Search Student
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Student Info */}
        {studentData && (() => {
          const filteredUnpaidDays = studentData.unpaid_days || [];
          const filteredUnpaidCount = filteredUnpaidDays.length;
          const filteredTotalOutstanding = filteredUnpaidDays.reduce((sum, p) => sum + Number(p.amount), 0);

          return (
            <>
              <Paper
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Student Information
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setOpenAddDateDialog(true)}
                    sx={{ borderColor: '#667eea', color: '#667eea' }}
                  >
                    + Add Fee Date
                  </Button>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Student ID</Typography>
                      <Typography variant="h6">{studentData.student.student_id}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Student Name</Typography>
                      <Typography variant="h6">{studentData.student.user?.name}</Typography>
                    </Box>
                 
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Last Payment Date</Typography>
                      <Typography variant="h6">
                        {studentData.last_payment_date
                          ? new Date(studentData.last_payment_date).toLocaleDateString()
                          : 'No payments yet'}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Unpaid Days
                      </Typography>
                      <Chip
                        label={`${filteredUnpaidCount} day${filteredUnpaidCount !== 1 ? 's' : ''}`}
                        color={filteredUnpaidCount > 0 ? 'warning' : 'success'}
                        sx={{ fontSize: '1rem', px: 2, py: 3 }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Outstanding
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#ef4444' }}>
                        Rs. {filteredTotalOutstanding.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Last Payment Details Card */}
              {studentData.last_payment && (
                <Paper
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                    border: '2px solid #10b981',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <History sx={{ fontSize: 32, color: '#10b981', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#065f46' }}>
                      Last Payment Details
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(255,255,255,0.7)', 
                          borderRadius: 2,
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CalendarToday sx={{ fontSize: 18, color: '#10b981', mr: 1 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Payment Date
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ color: '#065f46', fontWeight: 700 }}>
                          {new Date(studentData.last_payment.payment_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(255,255,255,0.7)', 
                          borderRadius: 2,
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <AccountBalanceWallet sx={{ fontSize: 18, color: '#10b981', mr: 1 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Amount Paid
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ color: '#065f46', fontWeight: 700 }}>
                          Rs. {Number(studentData.last_payment.amount).toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(255,255,255,0.7)', 
                          borderRadius: 2,
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PaymentIcon sx={{ fontSize: 18, color: '#10b981', mr: 1 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Payment Method
                          </Typography>
                        </Box>
                        <Chip 
                          label={(studentData.last_payment.payment_method || 'cash').replace('_', ' ').toUpperCase()}
                          size="small"
                          sx={{ 
                            bgcolor: '#10b981', 
                            color: 'white', 
                            fontWeight: 700,
                            textTransform: 'capitalize'
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(255,255,255,0.7)', 
                          borderRadius: 2,
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CalendarToday sx={{ fontSize: 18, color: '#10b981', mr: 1 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Class Date
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 600 }}>
                          {new Date(studentData.last_payment.class_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    </Grid>

                    {studentData.last_payment.remarks && (
                      <Grid item xs={12}>
                        <Box 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(255,255,255,0.7)', 
                            borderRadius: 2,
                            border: '1px solid rgba(16, 185, 129, 0.2)'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                            Remarks
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#065f46' }}>
                            {studentData.last_payment.remarks}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {/* Days Since Last Payment */}
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          bgcolor: filteredUnpaidCount >= 6 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.7)', 
                          borderRadius: 2,
                          border: filteredUnpaidCount >= 6 ? '2px solid #ef4444' : '1px solid rgba(16, 185, 129, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: filteredUnpaidCount >= 6 ? '#991b1b' : '#065f46' }}>
                            {filteredUnpaidCount === 0 && '✓ All caught up!'}
                            {filteredUnpaidCount > 0 && filteredUnpaidCount < 6 && `${filteredUnpaidCount} unpaid class${filteredUnpaidCount !== 1 ? 'es' : ''} since last payment`}
                            {filteredUnpaidCount >= 6 && `⚠️ ${filteredUnpaidCount} unpaid classes - Payment overdue!`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Last paid: {Math.floor((new Date() - new Date(studentData.last_payment.payment_date)) / (1000 * 60 * 60 * 24))} days ago
                          </Typography>
                        </Box>
                        {filteredUnpaidCount >= 6 && (
                          <Chip 
                            label="OVERDUE" 
                            color="error" 
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* No Payment History Message */}
              {!studentData.last_payment && (
                <Paper
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: '2px solid #fbbf24',
                    textAlign: 'center'
                  }}
                >
                  <Warning sx={{ fontSize: 48, color: '#f59e0b', mb: 1 }} />
                  <Typography variant="h6" sx={{ color: '#92400e', fontWeight: 600, mb: 1 }}>
                    No Payment History
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d97706' }}>
                    This student has not made any payments yet
                  </Typography>
                </Paper>
              )}

              {/* Unpaid Days Table */}
              {filteredUnpaidDays.length > 0 && (
                <Paper
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Unpaid Days ({filteredUnpaidCount})
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setOpenPaymentDialog(true)}
                      disabled={selectedPayments.length === 0}
                      startIcon={<PaymentIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                        }
                      }}
                    >
                      Mark {selectedPayments.length} Payment{selectedPayments.length !== 1 ? 's' : ''}
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead sx={{ bgcolor: '#f9fafb' }}>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedPayments.length === filteredUnpaidDays.length && filteredUnpaidDays.length > 0}
                              indeterminate={selectedPayments.length > 0 && selectedPayments.length < filteredUnpaidDays.length}
                              onChange={handleSelectAll}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Class Date</TableCell>
                       
                          <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredUnpaidDays.map((payment) => (
                          <TableRow key={payment.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedPayments.includes(payment.id)}
                                onChange={() => handleSelectPayment(payment.id)}
                              />
                            </TableCell>
                            <TableCell>{new Date(payment.class_date).toLocaleDateString()}</TableCell>
                            <TableCell>Rs. {Number(payment.amount).toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip
                                label={payment.payment_status}
                                color={payment.payment_status === 'pending' ? 'warning' : 'error'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* No unpaid days message */}
              {filteredUnpaidDays.length === 0 && (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <CheckCircle sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    All Payments Up to Date!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This student has no outstanding payments.
                  </Typography>
                </Paper>
              )}
            </>
          );
        })()}

        {/* Payment Dialog */}
        <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Confirm Payment</Typography>
              <IconButton onClick={() => setOpenPaymentDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Student:</strong> {studentData?.student?.user?.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Days Selected:</strong> {selectedPayments.length}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>Total Amount:</strong> Rs. {
                  studentData?.unpaid_days
                    ?.filter(p => selectedPayments.includes(p.id))
                    ?.reduce((sum, p) => sum + Number(p.amount), 0)
                    ?.toFixed(2) || '0.00'
                }
              </Typography>

              <TextField
                select
                fullWidth
                label="Payment Method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="online">Online Payment</MenuItem>
              </TextField>

              <TextField
                fullWidth
                label="Remarks (Optional)"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleMarkPayments}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              }}
            >
              Confirm Payment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Payment Date Dialog */}
        <Dialog open={openAddDateDialog} onClose={() => setOpenAddDateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Add Fee Date</Typography>
              <IconButton onClick={() => setOpenAddDateDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box>
              {studentData && (
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <strong>Student:</strong> {studentData.student.user?.name} ({studentData.student.student_id})
                </Typography>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Subject: {selectedSubject}
                </Typography>
                <Typography variant="caption">
                  This fee date will be added for {selectedSubject} classes
                </Typography>
              </Alert>

              <TextField
                fullWidth
                type="date"
                label="Class Date"
                value={newPaymentDate}
                onChange={(e) => setNewPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <Alert severity="warning" sx={{ mt: 2 }}>
                This will create a new unpaid fee record for the selected date.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddDateDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddPaymentDate}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              Add Fee Date
            </Button>
          </DialogActions>
        </Dialog>

        {/* Subject Selection Dialog */}
        <Dialog
          open={openSubjectDialog}
          onClose={() => {}}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }
          }}
        >
          <DialogTitle
            sx={{
              color: 'white',
              textAlign: 'center',
              pt: 4,
              pb: 2
            }}
          >
            <MenuBook sx={{ fontSize: 60, mb: 2 }} />
            <Typography component="div" variant="h6" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
              Select Subject to Collect
            </Typography>
            <Typography component="div" variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              Choose which subject's fees you will be collecting today
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 4, px: 4 }}>
            <Grid container spacing={2}>
              {subjects.map((subject) => (
                <Grid item xs={12} key={subject}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleSubjectSelect(subject)}
                    sx={{
                      py: 2,
                      bgcolor: 'white',
                      color: '#667eea',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default FundCollectorDashboard;
